import * as React from "react";
import { useEffect, useState, type InputHTMLAttributes } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/design-system/primitives";
import { Icon } from "@/lib/Icon";
import { cn } from "@/lib/cn";
import { loginDestination } from "@/lib/auth/loginDestination";
import { brand } from "@brand";
import { LoginTopBar } from "./LoginTopBar";

/** Storefront (`brand.storefrontSignIn`): Supabase auth errors as sentences
 *  with a next step. The house build shows the raw message. */
type AuthStep = "send" | "code" | "password" | "reset";

function authErrorText(err: unknown): { code: string; message: string } {
  const e = (err ?? {}) as { code?: unknown; message?: unknown };
  return {
    code: typeof e.code === "string" ? e.code : "",
    message: typeof e.message === "string" ? e.message : "",
  };
}

/** An email Supabase will not send a sign-in link to because it has no
 *  account ("Signups not allowed for otp"): `shouldCreateUser: false`. */
function isUnknownAccountError(err: unknown): boolean {
  const { code, message } = authErrorText(err);
  return (
    code === "otp_disabled" ||
    code === "signup_disabled" ||
    code === "user_not_found" ||
    /signups? not allowed|user not found/i.test(message)
  );
}

function storefrontAuthMessage(err: unknown, step: AuthStep): string {
  const { code, message } = authErrorText(err);
  if (
    code === "over_email_send_rate_limit" ||
    code === "over_request_rate_limit" ||
    /rate limit|security purposes|too many/i.test(message)
  ) {
    return "Too many tries in a short time. Wait a minute, then try again.";
  }
  if (step === "code") {
    return "That code didn't work or has expired. Go back and send a new link.";
  }
  if (step === "password" && (code === "invalid_credentials" || /invalid login/i.test(message))) {
    return "That email and password don't match. Try again, use a sign-in link instead, or reset your password.";
  }
  if (/network|fetch/i.test(message)) {
    return "We couldn't reach the server. Check your connection and try again.";
  }
  return step === "reset"
    ? "We couldn't send the reset email. Try again in a moment."
    : step === "password"
      ? "We couldn't sign you in. Try again in a moment."
      : "We couldn't send the email. Try again in a moment.";
}

// Magic-link-first, with a password fallback for existing accounts. Patient
// accounts are minted by the lander purchase flow (Stripe + Healthie chart
// created server-side), so /login MUST NOT support self-signup —
// `shouldCreateUser:false` blocks the "type any email, get a portal account"
// loophole. Password sign-in (`signInWithPassword`) only authenticates
// EXISTING users and never creates accounts, so it's safe here — and it's the
// scanner-proof path for corporate/investor inboxes whose mail gateways
// prefetch + redeem the magic link, which also kills the shared OTP code.
type Mode = "signin" | "magic_link_sent" | "password" | "reset_sent";

export function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath =
    (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  const postLoginPath = loginDestination(redirectPath);
  const authLinkError = Boolean((location.state as { authLinkError?: boolean } | null)?.authLinkError);
  const [mode, setMode] = useState<Mode>("signin");
  // Prefill from `?email=` when present — system-web's /login routes
  // unknown-to-CP users here with the email in the query string so they
  // don't have to retype it. SSR-safe via the typeof window check.
  const [email, setEmail] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("email") || "";
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");

  // Already signed in? Don't make them sign in again. /login is reachable
  // from links that don't know the reader's auth state — the expired
  // payment-link CTA, emailed deep links, a bookmark — and showing the
  // form to someone who already has a session is a dead end: they enter
  // their email, wait for a magic link they don't need, and never reach
  // the page the link promised. Honors `state.from`, so the expired-link
  // CTA still lands on the payment form.
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled && data.session) {
        navigate(postLoginPath, { replace: true });
      }
    }).catch(() => {
      // Keep ordinary sign-in usable if recovering a bad callback fails.
    });
    return () => {
      cancelled = true;
    };
  }, [navigate, postLoginPath]);

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!email) {
      setError("Enter your email first.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}${postLoginPath}`,
          shouldCreateUser: false,
        },
      });
      if (error) throw error;
      setMode("magic_link_sent");
    } catch (err) {
      if (brand.storefrontSignIn && isUnknownAccountError(err)) {
        // The same screen as a sent link: sign-in never says which emails
        // have an account (a store's patient list is health information).
        setMode("magic_link_sent");
      } else if (brand.storefrontSignIn) {
        setError(storefrontAuthMessage(err, "send"));
      } else {
        setError(err instanceof Error ? err.message : "Magic link send failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPassword = async () => {
    setError(null);
    if (!email) {
      setError("Enter your email above first.");
      return;
    }
    setBusy(true);
    try {
      // Recovery emails only ever go to EXISTING auth users — Supabase's
      // reset API cannot create accounts, so the no-self-signup rule
      // holds. Unknown emails no-op without an error (no enumeration);
      // the sent-screen copy is phrased accordingly.
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setMode("reset_sent");
    } catch (err) {
      setError(
        brand.storefrontSignIn
          ? storefrontAuthMessage(err, "reset")
          : err instanceof Error
            ? err.message
            : "Couldn't send the reset email",
      );
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordSubmit = async (
    e: React.SyntheticEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError("Enter your email and password.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      // Honor the deep link the guard captured (same as the magic-link
      // path's emailRedirectTo) — a patient who tapped an article link
      // should land on the article, not /app home.
      navigate(postLoginPath, { replace: true });
    } catch (err) {
      setError(
        brand.storefrontSignIn
          ? storefrontAuthMessage(err, "password")
          : err instanceof Error
            ? err.message
            : "Sign-in failed",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      {authLinkError && (
        <p role="status" className="text-16 text-ink-secondary mb-20">
          That sign-in link has expired or was already used. Sign in below to continue to your page.
        </p>
      )}
      {mode === "magic_link_sent" ? (
        <CheckEmail
          email={email}
          onBack={() => setMode("signin")}
          onVerified={() => navigate(postLoginPath, { replace: true })}
        />
      ) : mode === "reset_sent" ? (
        <ResetEmailSent
          email={email}
          onBack={() => {
            setError(null);
            setMode("password");
          }}
        />
      ) : mode === "password" ? (
        <PasswordForm
          email={email}
          password={password}
          error={error}
          busy={busy}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onSubmit={handlePasswordSubmit}
          onForgot={handleForgotPassword}
          onUseLink={() => {
            setError(null);
            setMode("signin");
          }}
        />
      ) : (
        <SignInForm
          email={email}
          error={error}
          busy={busy}
          postLoginPath={postLoginPath}
          onEmailChange={setEmail}
          onSubmit={handleSubmit}
          onUsePassword={() => {
            setError(null);
            setMode("password");
          }}
        />
      )}
    </Shell>
  );
}

/* ---------- Layout shell ---------- */

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-white text-black">
      <LoginTopBar />
      <main className="flex-1 flex flex-col px-15 md:px-30">
        <div className="w-full max-w-[44rem] mx-auto flex-1 flex flex-col justify-center pt-60 pb-60 md:pt-100 md:pb-100">
          {children}
        </div>
      </main>
    </div>
  );
}


/* ---------- Form states ---------- */

interface SignInFormProps {
  email: string;
  error: string | null;
  busy: boolean;
  postLoginPath: string;
  onEmailChange: (v: string) => void;
  onSubmit: (e: React.SyntheticEvent<HTMLFormElement>) => void;
  onUsePassword: () => void;
}

function SignInForm({
  email,
  error,
  busy,
  postLoginPath,
  onEmailChange,
  onSubmit,
  onUsePassword,
}: SignInFormProps) {
  return (
    <div className="flex flex-col gap-25">
      <h1 className="type-headline-lg text-black">Sign in to your account</h1>
      <p className="font-body text-15 text-[#7C7C7C]">
        Enter the email you used at checkout. We&apos;ll send you a one-tap
        sign-in link.
      </p>

      <form onSubmit={onSubmit} className="flex flex-col gap-15">
        <Field
          type="email"
          placeholder="Email address"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
        />

        {error && (
          <div role="alert" className="font-body text-13 text-danger">
            {error}
          </div>
        )}

        <Button type="submit" variant="filled" loading={busy} className="mt-5">
          <span>Email me a sign-in link</span>
          <span className="flex items-center justify-center w-8">
            <Icon name="Arrow" viewBox="0 0 10 12" className="w-8" />
          </span>
        </Button>

        <button
          type="button"
          onClick={onUsePassword}
          className="self-start font-body text-13 text-[#7C7C7C] hover:text-black"
        >
          Use a password instead
        </button>

        {/* DEV-ONLY: one-click sign in with local test accounts.
            Stripped from production builds via import.meta.env.DEV. */}
        {import.meta.env.DEV && (
          <DevSignInButton
            postLoginPath={postLoginPath}
            accounts={[
              { email: "jake@systemlabs.com", password: "SystemDev123!" },
              { email: "eden@systemlabs.com", password: "SystemDev123!" },
            ]}
          />
        )}
      </form>
    </div>
  );
}

/** Password sign-in for existing accounts. Scanner-proof fallback for
 *  corporate/investor inboxes whose mail gateways redeem the magic link
 *  (and its shared OTP code) before the user can. Never creates accounts. */
interface PasswordFormProps {
  email: string;
  password: string;
  error: string | null;
  busy: boolean;
  onEmailChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onSubmit: (e: React.SyntheticEvent<HTMLFormElement>) => void;
  onForgot: () => void;
  onUseLink: () => void;
}

function PasswordForm({
  email,
  password,
  error,
  busy,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onForgot,
  onUseLink,
}: PasswordFormProps) {
  return (
    <div className="flex flex-col gap-25">
      <h1 className="type-headline-lg text-black">Sign in to your account</h1>
      <p className="font-body text-15 text-[#7C7C7C]">
        Enter the email you used at checkout and your password.
      </p>

      <form onSubmit={onSubmit} className="flex flex-col gap-15">
        <Field
          type="email"
          placeholder="Email address"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
        />
        <Field
          type="password"
          placeholder="Password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
        />

        {error && (
          <div role="alert" className="font-body text-13 text-danger">
            {error}
          </div>
        )}

        <Button type="submit" variant="filled" loading={busy} className="mt-5">
          <span>Sign in</span>
          <span className="flex items-center justify-center w-8 h-10">
            <Icon name="Arrow" viewBox="0 0 10 12" className="w-8 h-10" />
          </span>
        </Button>
      </form>

      <FooterLinks
        left={
          <button type="button" onClick={onUseLink} className="footer-link">
            Use a sign-in link instead
          </button>
        }
        right={
          <button type="button" onClick={onForgot} className="footer-link">
            Forgot password?
          </button>
        }
      />
    </div>
  );
}

function ResetEmailSent({
  email,
  onBack,
}: {
  email: string;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col gap-25">
      <h1 className="type-headline-lg text-black">Check your email</h1>
      <p className="font-body text-15 text-[#7C7C7C]">
        If an account exists for <span className="text-black">{email}</span>,
        a password-reset link is on its way. Open it on this device to set a
        new password.
      </p>
      <FooterLinks
        left={
          <button type="button" onClick={onBack} className="footer-link">
            Back to sign in
          </button>
        }
      />
    </div>
  );
}

/** Dev-only one-click sign-in for local test accounts. Avoids the magic-link
 *  round-trip every reload. Only mounted when `import.meta.env.DEV`. */
function DevSignInButton({
  accounts,
  postLoginPath,
}: {
  accounts: { email: string; password: string }[];
  postLoginPath: string;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const signIn = async (email: string, password: string) => {
    setErr(null);
    setBusy(email);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      navigate(postLoginPath, { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Dev sign-in failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-15 border-t border-dashed border-[#ECECEC] pt-15 flex flex-col gap-10">
      <div className="font-mono text-10 uppercase text-[#7C7C7C]">
        Dev shortcut
      </div>
      {accounts.map(({ email, password }) => (
        <button
          key={email}
          type="button"
          onClick={() => signIn(email, password)}
          disabled={busy !== null}
          className="footer-link self-start"
        >
          {busy === email ? "Signing in…" : `Sign in as ${email}`}
        </button>
      ))}
      {err && (
        <div className="font-body text-13 text-danger">{err}</div>
      )}
    </div>
  );
}

/** Storefront: the sent screen for every email, with or without an
 *  account, and what to do when no email arrives. */
function StorefrontSentNote({ email }: { email: string }) {
  // The neutral fallback address (signed out, bootstrap and the store's
  // own address are not readable here).
  const supportEmail = brand.supportEmail;
  return (
    <div className="flex flex-col gap-10 font-body text-15 text-[#7C7C7C]">
      <p>
        If {email} has an order with us, we sent it a sign-in link. Open the
        link on this device to finish signing in.
      </p>
      <p>
        No email after a few minutes? Check your spam folder, and use the email
        from your order confirmation. Still stuck? Email{" "}
        <a
          href={`mailto:${supportEmail}`}
          className="text-black underline underline-offset-2 [overflow-wrap:anywhere]"
        >
          {supportEmail}
        </a>
        .
      </p>
    </div>
  );
}

/** Post-send screen: tap the emailed link, OR enter the code from the same
 *  email. The code path (verifyOtp) sidesteps link-redemption failures —
 *  expired/single-use/scanner-prefetched links, or opening the link on a
 *  different device. */
function CheckEmail({
  email,
  onBack,
  onVerified,
}: {
  email: string;
  onBack: () => void;
  onVerified: () => void;
}) {
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleVerify = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    const token = code.replace(/\s/g, "");
    if (!token) {
      setErr("Enter the code from the email.");
      return;
    }
    setErr(null);
    setVerifying(true);
    try {
      // `type: 'email'` matches the OTP issued by signInWithOtp (the same
      // token the magic link carries). On success the client persists the
      // session, so we can route straight into the app.
      const { error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: "email",
      });
      if (error) throw error;
      onVerified();
    } catch (e2) {
      setErr(
        brand.storefrontSignIn
          ? storefrontAuthMessage(e2, "code")
          : e2 instanceof Error
            ? e2.message
            : "That code didn't work — it may have expired. Request a new link.",
      );
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="flex flex-col gap-25">
      <h1 className="type-headline-lg text-black">Check your email</h1>
      {brand.storefrontSignIn ? (
        <StorefrontSentNote email={email} />
      ) : (
        <div className="font-body text-15 text-[#7C7C7C]">
          We sent a sign-in link to {email}. Open it on this device to finish
          signing in.
        </div>
      )}

      <form onSubmit={handleVerify} className="flex flex-col gap-15">
        <div className="font-mono text-10 uppercase text-[#7C7C7C]">
          {brand.storefrontSignIn
            ? "If your email shows a sign-in code, you can enter it here"
            : "Or enter the code from the email"}
        </div>
        <Field
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="Sign-in code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />

        {err && (
          <div role="alert" className="font-body text-13 text-danger">
            {err}
          </div>
        )}

        <Button
          type="submit"
          variant="filled"
          loading={verifying}
          disabled={!code.trim()}
        >
          <span>Sign in with code</span>
          <span className="flex items-center justify-center w-8 h-10">
            <Icon name="Arrow" viewBox="0 0 10 12" className="w-8 h-10" />
          </span>
        </Button>
      </form>

      <FooterLinks
        left={
          <button type="button" onClick={onBack} className="footer-link">
            Back to sign in
          </button>
        }
      />
    </div>
  );
}

/* ---------- Form primitives ---------- */

/** Portal form input — square, soft-gray surface, hairline border.
 *  bg #FAFAFA · border #ECECEC · text #7C7C7C · py-12 px-15 · focus → solid black border.
 */
function Field({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        "bg-[#FAFAFA] border border-[#ECECEC] text-[#7C7C7C] placeholder:text-[#7C7C7C]",
        "rounded-none outline-none py-12 px-15 w-full min-w-0",
        "focus:outline-none",
        "font-body text-15",
        className,
      )}
    />
  );
}

function FooterLinks({
  left,
  right,
}: {
  left?: React.ReactNode;
  right?: React.ReactNode;
}) {
  if (!left && !right) return null;
  return (
    <div className="mt-15 flex items-center justify-start gap-15">
      {left}
      {left && right && (
        <span aria-hidden className="font-mono text-10 text-black/40">
          |
        </span>
      )}
      {right}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * RESTORE WHEN PASSWORD AUTH IS LIVE
 *
 * Below is the full email+password sign-in/up form this screen used to ship
 * with. Re-enable by deleting the comment wrapper and re-wiring the imports
 * (`useNavigate`, `firstName`/`lastName`/`password` state). Keep
 * `shouldCreateUser: false` on the magic-link path though — it's a
 * meaningful security improvement independent of password support.
 *
 * NOTE: when re-enabling signup, the lander flow still needs to be the
 * canonical entry point for paid patients (it pre-creates Stripe customer +
 * Healthie chart). This password-signup branch is for free-tier users only.
 *
 * import { useNavigate } from 'react-router-dom';
 * type Mode = 'signin' | 'signup' | 'magic_link_sent';
 * const navigate = useNavigate();
 * const [password, setPassword] = useState('');
 * const [firstName, setFirstName] = useState('');
 * const [lastName, setLastName] = useState('');
 *
 * async function handleSubmit(e: FormEvent) {
 *   e.preventDefault();
 *   setError(null);
 *   setBusy(true);
 *   try {
 *     if (mode === 'signup') {
 *       const { error } = await supabase.auth.signUp({
 *         email,
 *         password,
 *         options: {
 *           data: {
 *             first_name: firstName.trim(),
 *             last_name: lastName.trim(),
 *             full_name: `${firstName.trim()} ${lastName.trim()}`.trim(),
 *           },
 *         },
 *       });
 *       if (error) throw error;
 *       navigate('/app', { replace: true });
 *     } else {
 *       const { error } = await supabase.auth.signInWithPassword({ email, password });
 *       if (error) throw error;
 *       navigate('/app', { replace: true });
 *     }
 *   } catch (err) {
 *     setError(err instanceof Error ? err.message : 'Sign-in failed');
 *   } finally {
 *     setBusy(false);
 *   }
 * }
 *
 * // JSX (insert above the magic-link button inside <form>):
 * {mode === 'signup' && (
 *   <div className="flex gap-15">
 *     <label className="flex flex-col gap-5 flex-1">
 *       <span className="type-label-sm text-ink-secondary">First name</span>
 *       <input type="text" autoComplete="given-name" required
 *         value={firstName} onChange={(e) => setFirstName(e.target.value)}
 *         className="h-40 px-15 bg-raised border border-line-subtle rounded-md" />
 *     </label>
 *     <label className="flex flex-col gap-5 flex-1">
 *       <span className="type-label-sm text-ink-secondary">Last name</span>
 *       <input type="text" autoComplete="family-name" required
 *         value={lastName} onChange={(e) => setLastName(e.target.value)}
 *         className="h-40 px-15 bg-raised border border-line-subtle rounded-md" />
 *     </label>
 *   </div>
 * )}
 * <label className="flex flex-col gap-5">
 *   <span className="type-label-sm text-ink-secondary">Password</span>
 *   <input type="password"
 *     autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
 *     required minLength={8}
 *     value={password} onChange={(e) => setPassword(e.target.value)}
 *     className="h-40 px-15 bg-raised border border-line-subtle rounded-md" />
 * </label>
 * <div className="flex items-center gap-15 mt-10 flex-wrap">
 *   <Button type="submit" loading={busy}>
 *     {mode === 'signin' ? 'Sign in' : 'Create account'} →
 *   </Button>
 *   <Button type="button" variant="ghost"
 *     onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}>
 *     {mode === 'signin' ? 'Need an account?' : 'Have an account?'}
 *   </Button>
 * </div>
 * -------------------------------------------------------------------------- */
